import React, { useState } from "react";
import axios from "axios";
import {
  BsExclamationTriangleFill,
  BsX,
  BsCheckCircleFill,
} from "react-icons/bs";

const BASE = "https://eps.elkomprintama.com/";

const isValidNum = (s) =>
  String(s).trim() !== "" && !isNaN(Number(String(s).trim()));

function FGEditModal({ record, onClose, onSaved }) {
  const [qty, setQty] = useState(record.qty != null ? String(record.qty) : "");
  const [finishQty, setFinishQty] = useState(
    record.finish_qty != null ? String(record.finish_qty) : "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const qtyBad = qty.trim() !== "" && !isValidNum(qty);
  const finishBad = finishQty.trim() !== "" && !isValidNum(finishQty);
  const canSave =
    !saving &&
    !saved &&
    !qtyBad &&
    !finishBad &&
    qty.trim() !== "" &&
    finishQty.trim() !== "";

  const handleSave = async () => {
    if (!isValidNum(qty) || !isValidNum(finishQty)) {
      setError("Both values must be valid numbers (e.g. 5000 or 5000.5).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await axios.patch(`${BASE}patch/api/fin_orders/${record.fg_id}/qty`, {
        qty: Number(qty),
        finish_qty: Number(finishQty),
      });
      setSaved(true);
      onSaved(record.fg_id, Number(qty), Number(finishQty));
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e.response?.data?.msg || "Save failed — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-indigo-700 rounded-t-2xl flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-base">Edit FG Record</h2>
            <p className="text-indigo-200 text-xs font-mono">
              FG #{record.fg_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-indigo-200 transition"
          >
            <BsX size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Read-only context */}
          <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
            <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
              Job
            </p>
            <p className="text-sm font-semibold text-gray-800 truncate">
              {record.job_name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {record.nme}
              {record.size ? ` · ${record.size}` : ""}
            </p>
          </div>

          {/* Editable qty fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Order Qty
              </label>
              <p className="text-[10px] text-gray-400 mb-1 font-mono">
                raw: {String(record.qty)}
              </p>
              <input
                type="text"
                inputMode="decimal"
                name="fg_edit_qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${
                  qtyBad
                    ? "border-red-400 bg-red-50 focus:ring-red-400 text-red-600"
                    : "border-gray-300 focus:ring-indigo-400"
                }`}
                placeholder="e.g. 5000"
              />
              {qtyBad && (
                <p className="text-xs text-red-500 mt-0.5">
                  Not a valid number
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Finish Qty
              </label>
              <p className="text-[10px] text-gray-400 mb-1 font-mono">
                raw: {String(record.finish_qty)}
              </p>
              <input
                type="text"
                inputMode="decimal"
                name="fg_edit_finish_qty"
                value={finishQty}
                onChange={(e) => setFinishQty(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${
                  finishBad
                    ? "border-red-400 bg-red-50 focus:ring-red-400 text-red-600"
                    : "border-gray-300 focus:ring-indigo-400"
                }`}
                placeholder="e.g. 5000"
              />
              {finishBad && (
                <p className="text-xs text-red-500 mt-0.5">
                  Not a valid number
                </p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-400">
            Use plain numbers — no dot thousands separator (type{" "}
            <span className="font-mono">5000</span>, not{" "}
            <span className="font-mono">5.000</span>).
          </p>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              <BsExclamationTriangleFill
                className="text-red-400 mt-0.5 flex-shrink-0"
                size={13}
              />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {saved && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2">
              <BsCheckCircleFill
                className="text-green-500 flex-shrink-0"
                size={14}
              />
              <p className="text-xs text-green-700 font-semibold">
                Saved successfully!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FGEditModal;
